import { zodResolver } from '@hookform/resolvers/zod';
import { occupationSchema, type Occupation } from '@pgs/shared';
import { useMemo } from 'react';
import { useController, useForm } from 'react-hook-form';
import { focusFirstError } from '../focusFirstError';
import { useSectors } from '../hooks/useSectors';
import { NavButtons } from '../NavButtons';
import { StepPanel } from '../StepPanel';
import { DateInput } from '@/components/form/DateInput';
import { SelectField } from '@/components/form/SelectField';
import { cx } from '@/components/form/utils';

interface Props {
  defaultValues?: Partial<Occupation>;
  onBack: () => void;
  onSubmit: (values: Occupation) => void;
}

export const Step4Occupation = ({ defaultValues, onBack, onSubmit }: Props) => {
  const { data: sectors } = useSectors();
  const {
    register,
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<Occupation>({
    resolver: zodResolver(occupationSchema),
    defaultValues: { cis: false, ...defaultValues },
    mode: 'onBlur',
  });

  // Bind the Yes/No radio directly to the boolean `cis` field so form state
  // always matches the schema (no helper string field, no post-validation
  // synthesis).
  const cisField = useController({ name: 'cis', control });
  const cisValue = cisField.field.value;

  const sectorName = watch('sector');
  const currentSector = useMemo(
    () => sectors?.find((s) => s.name === sectorName),
    [sectors, sectorName],
  );

  // Education and Healthcare should appear first, then the remaining sectors
  // in their original order.
  const PRIORITY_SECTORS = ['Education', 'Healthcare'];
  const orderedSectors = useMemo(() => {
    if (!sectors) return [];
    const priority = PRIORITY_SECTORS.flatMap((n) => sectors.filter((s) => s.name === n));
    const rest = sectors.filter((s) => !PRIORITY_SECTORS.includes(s.name));
    return [...priority, ...rest];
  }, [sectors]);

  return (
    <StepPanel icon="fa-briefcase" title="Your Occupation">
      <form onSubmit={handleSubmit(onSubmit, focusFirstError)}>
        <SelectField
          label="Sector"
          {...register('sector')}
          placeholder="Select your sector..."
          options={orderedSectors.map((s) => ({ value: s.name, label: s.name }))}
          error={errors.sector?.message}
        />
        <SelectField
          label="Job Role"
          {...register('role')}
          disabled={!currentSector}
          placeholder={currentSector ? 'Select your role...' : 'Select your sector first...'}
          options={
            currentSector
              ? [...currentSector.roles].sort((a, b) => a.localeCompare(b)).map((r) => ({
                  value: r,
                  label: r,
                }))
              : []
          }
          error={errors.role?.message}
        />
        <DateInput control={control} name="startDate" label="When Do You Expect To Start Work" />
        <div>
          <label className="lbl">Do You Work In The Construction Industry?</label>
          {[
            { value: true, label: 'Yes' },
            { value: false, label: 'No' },
          ].map((o) => (
            <label
              key={o.label}
              className={cx('radio-opt', cisValue === o.value && 'selected')}
            >
              <input
                type="radio"
                checked={cisValue === o.value}
                onChange={() => cisField.field.onChange(o.value)}
                onBlur={cisField.field.onBlur}
                className="mt-0.5 h-[15px] w-[15px] accent-teal"
              />
              <span className="text-[11px] font-bold uppercase tracking-wide text-ink-700">
                {o.label}
              </span>
            </label>
          ))}
        </div>
        <NavButtons onBack={onBack} submit />
      </form>
    </StepPanel>
  );
};
